import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X, KeyRound, Loader2, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters");
    if (next !== confirm) return setError("Passwords do not match");
    if (next === current) return setError("New password must differ from current");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setDone(true);
      toast.success("Password changed");
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="change-password-dialog">
      <form onSubmit={submit} className="bg-white border border-slate-300 w-full max-w-md my-16">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center">
              <KeyRound className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-2xl">Change Password</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="change-password-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 flex items-center gap-3" data-testid="change-password-success">
            <div className="w-10 h-10 bg-green-600 text-white flex items-center justify-center">
              <Check className="w-5 h-5" />
            </div>
            <div className="font-display text-xl text-slate-900">Password updated</div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">Current password</label>
                <input
                  type={show ? "text" : "password"}
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  data-testid="change-current"
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">New password</label>
                <input
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  data-testid="change-new"
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                />
                <div className="text-xs text-slate-500 mt-1">Minimum 8 characters.</div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">Confirm new password</label>
                <input
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="change-confirm"
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                />
              </div>
              <button
                type="button"
                onClick={() => setShow(!show)}
                data-testid="change-toggle-visibility"
                className="text-xs uppercase tracking-widest text-slate-600 flex items-center gap-2 hover:text-slate-900"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {show ? "Hide passwords" : "Show passwords"}
              </button>
              {error && (
                <div className="text-sm text-red-600 border-l-4 border-red-600 bg-red-50 px-3 py-2" data-testid="change-error">{error}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-300 text-sm uppercase tracking-widest" data-testid="change-cancel">
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-5 py-2 bg-slate-900 text-white text-sm uppercase tracking-widest hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-60"
                data-testid="change-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Update password
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
